import TextField from "@/common/elements/InputElements/TextField";
import React, { useEffect, useState } from "react";
import Button from "@/common/elements/Button";
import Select from "@/common/elements/InputElements/Select";
import {
  MedicalModalHeaderContainer,
  QuestionnaireModalBodyContainer,
  SectionHeading,
} from "./styles";
import { Form } from "antd";
import Modal from "@/common/elements/Modal";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import {
  getOptionsFromRefList,
  isNonEmptyVariable,
} from "@/util/common_function";
import CommonDatePicker from "@/common/elements/DatePicker";
import dayjs, { isDayjs } from "dayjs";
import _ from "lodash";
import { useSelector } from "react-redux";
import { WarningOutlined } from "@ant-design/icons";
import {
  computePoints,
  computeAutoSetAnswers,
  computeHilites,
  isQuestionVisible,
} from "@/util/questionnaireEngine";

// How many auto-set passes to resolve in one recompute before giving up -
// mirrors ASP's documented "second pass" for side effects/auto-sets that
// cascade off each other, capped as a safety guard against a pathological
// config loop.
const MAX_AUTOSET_PASSES = 5;

// Config-driven recompute: given a questionnaire and its current answers,
// derives points (per-question/per-group/total), resolves any Set-Answer-
// Based-On-Points auto-set rules (cascading up to MAX_AUTOSET_PASSES, since
// an auto-set answer can itself feed a group/total that another auto-set
// rule reads), and evaluates Hilite On/Off. Pure function - no question-ID or
// questionnaire-name special-casing, works for any questionnaire's config.
function recomputeQuestionnaire(questionnaireData, questionAnswers) {
  let currentAnswers = questionAnswers;
  let points = computePoints({
    questionnaire: questionnaireData,
    questionAnswers: currentAnswers,
  });
  let autoSetResults = [];

  for (let pass = 0; pass < MAX_AUTOSET_PASSES; pass++) {
    autoSetResults = computeAutoSetAnswers({
      questionnaire: questionnaireData,
      questionAnswers: currentAnswers,
      totalPoints: points.totalPoints,
      questionGroupPoints: points.questionGroupPoints,
    });

    let changed = false;
    currentAnswers = currentAnswers.map((qa) => {
      const forcedMatch = autoSetResults.find(
        (r) => r.questionId === qa.questionId,
      );
      if (forcedMatch && qa.answer !== forcedMatch.answer) {
        changed = true;
        return { ...qa, answer: forcedMatch.answer };
      }
      return qa;
    });

    if (!changed) break;
    points = computePoints({
      questionnaire: questionnaireData,
      questionAnswers: currentAnswers,
    });
  }

  const hilites = computeHilites({
    questionnaire: questionnaireData,
    questionAnswers: currentAnswers,
  });

  return { questionAnswers: currentAnswers, points, autoSetResults, hilites };
}

const ModalHeader = ({ questionnaire, onHide, totalPoints }) => {
  return (
    <>
      {/* <Button color="primary" type="primary" onClick={onHide}>
        Exit
      </Button> */}
      <div
        className="questionnaire-scores-container"
        style={{ marginLeft: "0px", marginRight: "0px" }}
      >
        <div className="main-score-container score-container">
          <div className="score-name">
            {questionnaire?.questionnaireName || ""}
          </div>
          <div className="score">
            <span className="points-caption">Total Points: </span>{" "}
            {totalPoints || 0}
          </div>
        </div>
      </div>
      {/* <div className="section-score-container score-container">
        <div className="score-name">{qg?.subheading}</div>
        <div className="score">
          <span className="points-caption">Total Points: </span>0
        </div>
      </div> */}
      {/* <div className="time">
        <img alt="Clock Icon" src={clock} /> N / A
      </div> */}
    </>
  );
};

const MedicalQuestionnaireModal = ({
  show,
  onHide,
  questionnaire,
  getAnswer,
  singleQuestionnaire,
  isEdit = false,
}) => {
  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state?.bookings,
  );
  const [form] = Form.useForm();
  const [initialValues, setInitialValues] = useState(null);
  const [questionnaireLocal, setQuestionnaireLocal] = useState(null);
  const [hilites, setHilites] = useState({});
  const values = Form.useWatch("questionAnswers", form);

  useEffect(() => {
    const copyQuestionnaire = _.cloneDeep(questionnaire);
    let group = copyQuestionnaire?.questionGroups?.map((g) => {
      return {
        ...g,
        questions: _.sortBy(g?.questions, "sequence"),
      };
    });
    group = _.sortBy(group, "sequence");

    if (copyQuestionnaire?.questionnaireID === 4) {
      if (bookings?.bio?.[0]?.sex === "M")
        group = group?.filter((g) => g?.questionGroupID !== 12);
    }

    group = updateQuestionSequence(group);

    const questionnaireNew = {
      ...copyQuestionnaire,
      questionGroups: group,
    };

    let ques = [];
    if (isEdit) {
      singleQuestionnaire?.questionnaireInstances?.forEach((item) => {
        item?.questionAnswers?.forEach((answer) => {
          ques.push({
            questionId: answer.questionId,
            answer: isNonEmptyVariable(answer?.answer)
              ? !isNaN(answer?.answer) && !isDayjs(answer?.answer)
                ? parseInt(answer?.answer)
                : answer?.answer
              : "",
          });
        });
      });
    } else {
      group?.forEach((qg) => {
        qg?.questions?.forEach((question) => {
          ques.push({ questionId: question.questionID, answer: "" });
        });
      });
    }

    // Config-driven recompute (points + auto-set + hilite) runs on load too,
    // both for a brand-new questionnaire and when re-opening a saved one -
    // matches ASP re-deriving AutoSet answers every time the form renders,
    // rather than only trusting whatever was last saved.
    applyRecomputeResult(questionnaireNew, ques);
  }, [questionnaire, singleQuestionnaire]);

  useEffect(() => {
    if (isNonEmptyVariable(initialValues)) {
      form.setFieldsValue(initialValues);
    } else {
      form.resetFields();
    }
  }, [initialValues]);

  function updateQuestionSequence(data) {
    let sequenceNumber = 1; // Start sequence from 1

    data?.forEach((group) => {
      group?.questions?.forEach((question) => {
        question.sequence = sequenceNumber++;
      });
    });

    return data;
  }

  const handleCancel = () => {
    onHide();
  };

  const handleSubmit = () => {
    form
      .validateFields()
      .then((value) => {
        const obj = {
          ...initialValues,
          questionnaireId: questionnaire?.questionnaireID,
        };
        const returnData = _.cloneDeep(obj);
        getAnswer(returnData);
        setInitialValues(null);
        form?.resetFields();
      })
      .catch((err) => {
        let requiredField = err?.errorFields?.[0]?.name?.[1];
        if (isNonEmptyVariable(requiredField)) {
          requiredField = parseInt(requiredField) + 1;
          setTimeout(() => {
            const firstErrorField = document.querySelector(
              `#ques-${requiredField}`,
            );

            firstErrorField?.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            firstErrorField?.focus();
          }, 300);
        }
      });
  };

  // Config-driven recompute, invoked after every answer change (and once on
  // load): re-derives points/auto-set/hilite from scratch for the given
  // answers (no incremental deltas to drift out of sync), then pushes the
  // result into questionnaireLocal (per-question point/forced annotations
  // used for rendering), initialValues (the wire shape Save reads), and
  // hilites (per-question highlight state).
  const applyRecomputeResult = (questionnaireData, questionAnswers) => {
    const {
      questionAnswers: finalAnswers,
      points,
      autoSetResults,
      hilites: nextHilites,
    } = recomputeQuestionnaire(questionnaireData, questionAnswers);

    const annotatedGroups = questionnaireData?.questionGroups?.map((g) => ({
      ...g,
      questions: g?.questions?.map((q) => ({
        ...q,
        answer: finalAnswers?.find((qa) => qa.questionId === q.questionID)
          ?.answer,
        point: points?.pointsByQuestionId?.[q.questionID] ?? 0,
        forced: autoSetResults?.some(
          (r) => r.questionId === q.questionID && r.forced,
        ),
      })),
    }));

    setQuestionnaireLocal({
      ...questionnaireData,
      questionGroups: annotatedGroups,
    });
    setHilites(nextHilites);
    // Setting initialValues (a new object every call) re-triggers the
    // `[initialValues]` effect below, which calls form.setFieldsValue and
    // resyncs every visible field - including any question whose answer just
    // changed purely from an auto-set rule, not only the one the user
    // interacted with directly.
    setInitialValues({
      questionAnswers: finalAnswers,
      questionGroupPoints: points?.questionGroupPoints,
      totalPoints: points?.totalPoints,
    });
  };

  const handleKeyDownChange = (e, qg, item) => {
    const value = e.target.value;
    const patchedAnswers = initialValues?.questionAnswers?.map((qa) =>
      qa.questionId === item.questionID ? { ...qa, answer: value } : qa,
    );
    applyRecomputeResult(questionnaireLocal, patchedAnswers);
  };

  const handleOnChange = (value, qg, item) => {
    const patchedAnswers = initialValues?.questionAnswers?.map((qa) =>
      qa.questionId === item.questionID ? { ...qa, answer: value } : qa,
    );
    applyRecomputeResult(questionnaireLocal, patchedAnswers);
  };

  const getDependenciesQuestion = (values, item) =>
    isQuestionVisible(item, values);

  const getValidation = (question) => {
    if (isNonEmptyVariable(question?.mandatory)) {
      if (isNonEmptyVariable(question?.dependsOnQuestionID)) {
        const val = initialValues?.questionAnswers?.find(
          (qa) => qa?.questionId === question?.dependsOnQuestionID,
        );
        if (
          isNonEmptyVariable(val) &&
          val?.answer === question?.dependsOnQuestionAnswerRefListItemID &&
          question?.mandatory
        ) {
          return [
            {
              required: true,
              message: "Field is required",
            },
          ];
        }
      } else {
        if (question?.mandatory) {
          return [
            {
              required: true,
              message: "Field is required",
            },
          ];
        }
      }
    }
  };

  const getQuestionPositinFromQuestionID = (
    questionaireQuestions,
    paramName = "",
    quesID = "",
  ) => {
    let retIndex = -1;
    if (isNonEmptyVariable(questionaireQuestions)) {
      retIndex = initialValues?.questionAnswers?.findIndex(
        (qa) => qa?.questionId === questionaireQuestions?.questionID,
      );
    }

    if (isNonEmptyVariable(quesID)) {
      retIndex = initialValues?.questionAnswers?.findIndex(
        (qa) => qa?.questionId === quesID,
      );
    }

    if (retIndex !== -1) {
      let name = [];
      if (isNonEmptyVariable(paramName)) {
        name = ["questionAnswers", retIndex, "questionId"];
      } else {
        name = ["questionAnswers", retIndex, "answer"];
      }

      return name;
    }
  };

  return (
    <Modal
      header=""
      show={show}
      designPattern="ant"
      handleCancel={handleCancel}
      handleSubmit={handleSubmit}
      onHide={handleCancel}
      width={"80%"}
      bodyStyle={{ padding: "20px" }}
      className="no-header questionnaire-modal"
    >
      <MedicalModalHeaderContainer>
        <ModalHeader
          questionnaire={questionnaire}
          totalPoints={initialValues?.totalPoints}
          onHide={handleCancel}
        />
      </MedicalModalHeaderContainer>
      <Form form={form}>
        {isNonEmptyVariable(questionnaireLocal) &&
          questionnaireLocal?.questionGroups?.map((qg) => {
            return (
              <QuestionnaireModalBodyContainer key={qg.questionGroupID}>
                <SectionHeading className="d-flex justify-content-between section-heading text-dark">
                  <h3>{qg?.subheading}</h3>
                  <h6 style={{ paddingRight: "10px" }}>
                    Total group points:{" "}
                    {initialValues?.questionGroupPoints?.find(
                      (qgp) => qgp?.questionGroupId === qg?.questionGroupID,
                    )?.totalPoints || 0}
                  </h6>
                </SectionHeading>
                <ul className="questions">
                  {qg?.questions?.map((item, quesIndex) => {
                    let sequence = item?.sequence;

                    return (
                      <li key={quesIndex} id={"ques-" + sequence}>
                        <FormItem
                          hidden
                          name={getQuestionPositinFromQuestionID(
                            item,
                            "questionId",
                          )}
                        >
                          <TextField disable={!bookingsFieldsEnabled} />
                        </FormItem>
                        {!isNonEmptyVariable(item?.dependsOnQuestionID) && (
                          <div
                            className={
                              hilites?.[item?.questionID]
                                ? "question-item hilited"
                                : "question-item"
                            }
                          >
                            <div className="question">
                              <span className="number">{sequence}.</span>{" "}
                              <span
                                className="question--text-label"
                                dangerouslySetInnerHTML={{
                                  __html: item?.questionText,
                                }}
                              ></span>
                            </div>
                            <div className="options-container">
                              {item?.interfaceTypeID === 50000001 && (
                                <FormItem
                                  name={getQuestionPositinFromQuestionID(item)}
                                  rules={getValidation(item)}
                                >
                                  <Select
                                    id={`question-${quesIndex}`}
                                    placeholder=" Select any option"
                                    options={getOptionsFromRefList(
                                      "YesNo",
                                      false,
                                      true,
                                      true,
                                    )}
                                    onChange={(value) => {
                                      handleOnChange(value, qg, item);
                                    }}
                                    showSearch={true}
                                    disabled={!bookingsFieldsEnabled}
                                  />
                                </FormItem>
                              )}

                              {item?.interfaceTypeID === 50000002 && (
                                <FormItem
                                  name={getQuestionPositinFromQuestionID(item)}
                                  rules={getValidation(item)}
                                >
                                  <TextField
                                    onChange={(e) =>
                                      handleKeyDownChange(e, qg, item)
                                    }
                                    disabled={!bookingsFieldsEnabled}
                                  />
                                </FormItem>
                              )}

                              {item?.interfaceTypeID === 50000003 && (
                                <FormItem
                                  name={getQuestionPositinFromQuestionID(item)}
                                  rules={getValidation(item)}
                                >
                                  <TextField
                                    inputType="textarea"
                                    onChange={(e) =>
                                      handleKeyDownChange(e, qg, item)
                                    }
                                    disabled={!bookingsFieldsEnabled}
                                  />
                                </FormItem>
                              )}

                              {item?.interfaceTypeID === 50000004 && (
                                <FormItem
                                  name={getQuestionPositinFromQuestionID(item)}
                                  rules={getValidation(item)}
                                >
                                  <Select
                                    id={`question-${quesIndex}`}
                                    placeholder=" Select any option"
                                    options={getOptionsFromRefList(
                                      item?.refListName,
                                      false,
                                      false,
                                      true,
                                      item?.selectRefListID,
                                    )}
                                    onChange={(value) => {
                                      handleOnChange(value, qg, item);
                                    }}
                                    showSearch={true}
                                    disabled={!bookingsFieldsEnabled || item?.forced}
                                  />
                                </FormItem>
                              )}

                              {item?.interfaceTypeID === 50000005 && (
                                <FormItem
                                  name={getQuestionPositinFromQuestionID(item)}
                                  rules={getValidation(item)}
                                >
                                  <CommonDatePicker
                                    id={`question-${quesIndex}`}
                                    placeholder="MM/DD/YYYY"
                                    format="MM/DD/YYYY"
                                    showTime={false}
                                    onChange={(value) => {
                                      if (isNonEmptyVariable(value)) {
                                        handleOnChange(value, qg, item);
                                      }
                                    }}
                                    disabled={!bookingsFieldsEnabled}
                                  />
                                </FormItem>
                              )}
                            </div>
                            <div className="point-container">
                              <div className="points">
                                {item?.point || 0}
                                <div className="pts">pts</div>
                              </div>
                              <div className="icon-container">
                                {hilites?.[item?.questionID] && (
                                  <WarningOutlined />
                                )}
                              </div>
                            </div>
                          </div>
                        )}

                        {isNonEmptyVariable(item?.dependsOnQuestionID) && (
                          <div
                            style={
                              getDependenciesQuestion(values, item)
                                ? { display: "flex" }
                                : { display: "none" }
                            }
                            className={
                              hilites?.[item?.questionID]
                                ? "question-item hilited"
                                : "question-item"
                            }
                          >
                            <div className="question">
                              <span className="number">{sequence}.</span>{" "}
                              <span className="question--text-label">
                                {item?.questionText}
                              </span>
                            </div>
                            <div className="options-container">
                              {item?.interfaceTypeID === 50000001 && (
                                <FormItem
                                  name={getQuestionPositinFromQuestionID(item)}
                                  rules={getValidation(item)}
                                >
                                  <Select
                                    id={`question-${quesIndex}`}
                                    placeholder=" Select any option"
                                    options={getOptionsFromRefList(
                                      "YesNo",
                                      false,
                                      true,
                                      true,
                                    )}
                                    onChange={(value) => {
                                      handleOnChange(value, qg, item);
                                    }}
                                    showSearch={true}
                                    disabled={!bookingsFieldsEnabled}
                                  />
                                </FormItem>
                              )}

                              {item?.interfaceTypeID === 50000002 && (
                                <FormItem
                                  name={getQuestionPositinFromQuestionID(item)}
                                  rules={getValidation(item)}
                                >
                                  <TextField
                                    onChange={(e) =>
                                      handleKeyDownChange(e, qg, item)
                                    }
                                    disabled={!bookingsFieldsEnabled}
                                  />
                                </FormItem>
                              )}

                              {item?.interfaceTypeID === 50000003 && (
                                <FormItem
                                  name={getQuestionPositinFromQuestionID(item)}
                                  rules={getValidation(item)}
                                >
                                  <TextField
                                    inputType="textarea"
                                    onChange={(e) =>
                                      handleKeyDownChange(e, qg, item)
                                    }
                                    disabled={!bookingsFieldsEnabled}
                                  />
                                </FormItem>
                              )}

                              {item?.interfaceTypeID === 50000004 && (
                                <FormItem
                                  name={getQuestionPositinFromQuestionID(item)}
                                  rules={getValidation(item)}
                                >
                                  <Select
                                    id={`question-${quesIndex}`}
                                    placeholder=" Select any option"
                                    options={getOptionsFromRefList(
                                      item?.refListName,
                                      false,
                                      false,
                                      true,
                                      item?.selectRefListID,
                                    )}
                                    onChange={(value) => {
                                      handleOnChange(value, qg, item);
                                    }}
                                    showSearch={true}
                                    disabled={!bookingsFieldsEnabled || item?.forced}
                                  />
                                </FormItem>
                              )}

                              {item?.interfaceTypeID === 50000005 && (
                                <FormItem
                                  name={getQuestionPositinFromQuestionID(item)}
                                  rules={getValidation(item)}
                                >
                                  <CommonDatePicker
                                    id={`question-${quesIndex}`}
                                    placeholder="MM/DD/YYYY"
                                    format="MM/DD/YYYY"
                                    showTime={false}
                                    onChange={(value) => {
                                      if (isNonEmptyVariable(value)) {
                                        handleOnChange(value, qg, item);
                                      }
                                    }}
                                    disabled={!bookingsFieldsEnabled}
                                  />
                                </FormItem>
                              )}
                            </div>
                            <div className="point-container">
                              <div className="points">
                                {item?.point || 0}
                                <div className="pts">pts</div>
                              </div>
                              <div className="icon-container">
                                {hilites?.[item?.questionID] && (
                                  <WarningOutlined />
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </QuestionnaireModalBodyContainer>
            );
          })}
      </Form>
    </Modal>
  );
};

export default MedicalQuestionnaireModal;
